import { type ComputedRef } from "vue"
import {
  type TestContext,
  addMethod,
  boolean,
  date,
  object,
  ref,
  string,
  type ArraySchema,
  type StringSchema,
  type DateSchema,
  type AnyObject,
  array,
} from "yup"

import { type EntityWithoutRelations, type AdditionalAddressForm } from "@/types"
import {
  type Commune,
  type AccessLevel,
  type User,
  AppointmentType,
  FeeCategory,
  GeneralFeeCategory,
  Repetition,
  Months,
  InstitutionType,
  PaymentMethod,
  GroupRole,
  FactorCalculationBase,
} from "@/graphql/types"
import { i18n } from "@/i18n"
import { useSessionStore } from "@/store/session"

import { isTimeAfterTime, TIME_REGEX_OPTIONAL } from "./date"
import { isValidBic, isValidIban } from "./misc"

// Matches only letters (A-Z, a-z), spaces, periods, and a range of Unicode characters for German accented letters (like Ä, Ö, Ü, and ß). Does not allow numbers.
export const alphaSpacePeriodRegex = /^[A-Za-z .\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u00FF\u1E9E]*$/

// Matches only letters (A-Z, a-z), spaces, periods, and a range of Unicode characters for German accented letters (like Ä, Ö, Ü, and ß). It does allow numbers.
export const alphaNumericSpacePeriodRegex =
  /^[A-Za-z0-9 .\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u00FF\u1E9E]*$/

export const installYupMethods = () => {
  addMethod<StringSchema>(string, "phone", function (this: StringSchema) {
    const MAX = 15
    return this.matches(
      /^(?:(?:\+\(?(\d{1,3})\)?[\s\-/]*)?(?:\(?(\d{1,4})\)?[\s\-/]*)?(\d{1,4}[\s\-/]*){1,4})?$/,
      i18n.global.t("validation.phone_invalid")
    ).max(MAX, i18n.global.t("validation.phone_max_length", { n: MAX }))
  })

  addMethod<StringSchema>(
    string,
    "matchesChars",
    function (this: StringSchema, regex: RegExp, allowedChars: string[]) {
      return this.matches(
        regex,
        i18n.global.t("validation.only_chars_allowed", { chars: allowedChars.join("") })
      )
    }
  )

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  addMethod<ArraySchema<any[] | null | undefined, AnyObject>>(
    array,
    "unique",
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    function (message, mapper = (a: any) => a) {
      return this.test("unique", message, (list) => {
        if (!list) return true

        list = list.map(mapper).filter((e) => e !== undefined)
        return list.length === new Set(list).size
      })
    }
  )

  addMethod<StringSchema>(string, "noNumbers", function (this: StringSchema) {
    return this.matches(/^[^0-9]*$/, i18n.global.t("validation.no_numbers_allowed"))
  })

  addMethod<StringSchema>(string, "noSpecialChars", function (this: StringSchema) {
    return this.matches(
      /^[a-zA-Z0-9 .\-&,\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u00FF\u1E9E]*$/,
      i18n.global.t("validation.no_special_chars_allowed")
    )
  })

  addMethod<StringSchema>(string, "password", function (this: StringSchema) {
    return this.min(8, i18n.global.t("validation.password.length"))
      .matches(/[A-Z]/, i18n.global.t("validation.password.upper_case"))
      .matches(/[a-z]/, i18n.global.t("validation.password.lower_case"))
      .matches(/\d/, i18n.global.t("validation.password.digit"))
      .matches(/[!@#$%^&*(),.?":{}|<>]/, i18n.global.t("validation.password.special_char"))
  })
  addMethod<StringSchema>(string, "iban", function (this: StringSchema) {
    return this.test(
      "iban",
      i18n.global.t("validation.invalid_iban"),
      function (value: string | undefined) {
        return value === "" || value === undefined || isValidIban(value)
      }
    )
  })
  addMethod<StringSchema>(string, "zipCode", function (this: StringSchema) {
    return this.test(
      "is-digits-only-zipcode",
      i18n.global.t("validation.zipCode_digits"),
      function (value: string | undefined) {
        if (!value) return true
        return /^\d*$/.test(value)
      }
    )
      .test(
        "is-min-zipcode",
        i18n.global.t("validation.zipCode_min", [4]),
        function (value: string | undefined) {
          if (!value) return true
          return value.length >= 4
        }
      )
      .test(
        "is-max-zipcode",
        i18n.global.t("validation.zipCode_max", [5]),
        function (value: string | undefined) {
          if (!value) return true
          return value.length <= 5
        }
      )
  })
  addMethod<StringSchema>(string, "bic", function (this: StringSchema) {
    return this.test(
      "bic",
      i18n.global.t("validation.invalid_bic"),
      function (value: string | undefined) {
        return !value || isValidBic(value)
      }
    )
  })
}

export const useInstitutionSchema = () => {
  const sessionStore = useSessionStore()
  return string().when([], {
    is: () => sessionStore.isUserOrganizationOrUnitOrHigher,
    then: (schema) => schema.required(i18n.global.t("validation.institution_required")),
  })
}

export const useSimpleAddressSchema = () =>
  object({
    street: string()
      .trim()
      .notRequired()
      .max(30, i18n.global.t("validation.street_max", [30])),
    zipCode: string().trim().zipCode().notRequired(),
    district: string()
      .trim()
      .noNumbers()
      .notRequired()
      .max(30, i18n.global.t("validation.street_max", [30])),
    city: string()
      .trim()
      .noNumbers()
      .notRequired()
      .max(30, i18n.global.t("validation.street_max", [30])),
    country: string().trim().noNumbers().notRequired(),
  })

export const useContactSchema = () =>
  object({
    phone: string().phone().notRequired(),
    email: useEmailAddressSchema().notRequired(),
    email2: useEmailAddressSchema().notRequired(),
    phoneWork: string().phone().notRequired(),
    phoneMobile: string().phone().notRequired(),
    letterSalutation: string().trim().notRequired(),
    website: string()
      .trim()
      .notRequired()
      .nullable()
      .max(100, i18n.global.t("validation.max_character", [100])),
  })

export const useEmailAddressSchema = () =>
  string().trim().email(i18n.global.t("validation.email_invalid"))

export const useUniqueNameSchema = (list?: { name?: string | null }[]) => ({
  name: string()
    .trim()
    .required(i18n.global.t("validation.name_required"))
    .test("unique-name", i18n.global.t("validation.name_used"), (value) => {
      return !value || !list?.length || !list.some((item) => item.name === value)
    }),
})

export const usePasswordSchema = () => ({
  password: string().required(i18n.global.t("validation.password.required")).password(),
  confirmPassword: string().oneOf(
    [ref("password")],
    i18n.global.t("validation.password.not_matching")
  ),
})

const minUsernameLength = 3
export const useUserSchema = (userList?: User[], isUserAdmin = false) => ({
  salutation: string().required(i18n.global.t("admin.benutzer.validation.salutation_required")),
  firstname: string()
    .trim()
    .required(i18n.global.t("admin.benutzer.validation.firstname_required")),
  lastname: string().trim().required(i18n.global.t("admin.benutzer.validation.lastname_required")),
  username: string()
    .trim()
    .required(i18n.global.t("admin.benutzer.validation.username_required"))
    .min(
      minUsernameLength,
      i18n.global.t("admin.benutzer.validation.username_min_length", { min: minUsernameLength })
    )
    .matches(
      // eslint-disable-next-line no-control-regex
      /^[^<>&"'$%!#?§,;:*~/\\|^=[\]{}()`\s\x00-\x1F\x7F]+$/,
      i18n.global.t("admin.benutzer.validation.username_invalid_format")
    )
    .test("unique-name", i18n.global.t("validation.username_used"), function (value) {
      const users = userList ?? []
      // Todo in [AKIT-????] we should not validate this in the frontend, this might work for admin users with the manual "admin" check, users with lower access levels do not have access to the full list of users.
      return !users.find((a) => a.username === value) && (isUserAdmin || value !== "admin")
    }),
  email: useEmailAddressSchema().required(
    i18n.global.t("admin.benutzer.validation.email_required")
  ),
  phone: string().phone().notRequired(),
  kibigKey: string().trim().notRequired().nullable(),
})

export const useDateSchema = () => date().typeError(i18n.global.t("validation.invalid_date"))

export const usePersonSchema = (localePrefix = "validation") =>
  object({
    firstname: string()
      .trim()
      .required(i18n.global.t(`${localePrefix}.firstname_required`))
      .min(1, i18n.global.t(`${localePrefix}.firstname_min`, 1))
      .max(24, i18n.global.t(`${localePrefix}.firstname_max`, [24])),
    middlename: string().trim().notRequired(),
    lastname: string()
      .trim()
      .required(i18n.global.t(`${localePrefix}.lastname_required`))
      .min(1, i18n.global.t(`${localePrefix}.lastname_min`, 1))
      .max(24, i18n.global.t(`${localePrefix}.lastname_max`, [24])),
    gender: string()
      .trim()
      .required(i18n.global.t(`${localePrefix}.gender_required`)),
    salutation: string().trim().notRequired(),
    title: string().trim().notRequired(),
    denomination: string().trim().notRequired(),
    citizenships: array(string().trim()).notRequired(),
    languages: array(string().trim()).notRequired(),
    familyLanguageIsNotGerman: boolean().notRequired(),
  })

export const useBirthSchema = (dateRequired = false) => {
  let date: DateSchema<Date | null | undefined, AnyObject, undefined, ""> = useDateSchema().max(
    new Date(),
    i18n.global.t("validation.today_date_max")
  )
  if (dateRequired) date = date.required(i18n.global.t("validation.birthday_required"))
  else date = date.notRequired()

  return object({
    date,
    name: string().trim().notRequired(),
    city: string().trim().noNumbers().notRequired(),
    country: string().trim().notRequired(),
  })
}

export const useExternalSiblingSchema = (siblingLastName = "") =>
  object({
    firstname: string().required(i18n.global.t("validation.name_required")),
    lastname: string()
      .required(i18n.global.t("validation.lastname_required"))
      .default(siblingLastName),
    birthdate: useDateSchema()
      .required(i18n.global.t("validation.date_required"))
      .max(new Date(), i18n.global.t("validation.today_date_max")),
  })

export const useMovementSchema = (localePrefix = "validation") =>
  object({
    entry: useDateSchema().required(i18n.global.t(`${localePrefix}.entry_required`)),
    exit: useDateSchema()
      .notRequired()
      .test(
        "is-after-entry",
        i18n.global.t(`${localePrefix}.exitDate_after_entryDate`),
        (value, context) => !value || new Date(value) >= context.parent.entry
      ),
    reason: string().notRequired(),
  })

export type PasswordSchema = {
  password: string
  confirmPassword: string
}

export type BaseUserSchema = {
  firstname: string
  lastname: string
  username: string
  email: string
  salutation: string
} & PasswordSchema

export type UserAccessLevelSchema = {
  accessLevel?: AccessLevel
  customerId?: string
  organizationId?: string
  organizationUnitId?: string
  institutionId?: string
  roleId: string
}

export const useInitialUserSchema = () => ({
  createInitialUser: boolean(),
  user: object().when("createInitialUser", ([createInitialUser], schema) =>
    createInitialUser
      ? schema.shape({
          ...useUserSchema(),
          ...usePasswordSchema(),
          ...{
            roleId: string().required(i18n.global.t("admin.benutzer.validation.role_required")),
          },
        })
      : schema
  ),
})

export type InitialUserSchema =
  | {
      createInitialUser: false
      user?: BaseUserSchema & {
        roleId: string
      }
    }
  | {
      createInitialUser: true
      user: BaseUserSchema & {
        roleId: string
      }
    }

export const useCommuneSchema = (keys?: ComputedRef<Set<string>>) => ({
  name: string().trim().required(i18n.global.t("validation.name_required")),
  communeKey: string()
    .trim()
    .required(i18n.global.t("stammdaten.gemeinden.validation.gemeindeschluessel_required"))
    .test(
      "unique-commune-key",
      i18n.global.t("stammdaten.gemeinden.validation.gemeindeschluessel_unique"),
      (value) => {
        return !value || !keys?.value ? true : !keys.value.has(value.trim().toLowerCase())
      }
    ),
  contactPerson: object({
    name: string().trim().notRequired(),
    contact: useContactSchema().notRequired(),
  }),
  address: useSimpleAddressSchema(),
  additionalAddress: object({ 0: string(), 1: string() }),
  phone: string().phone().notRequired(),
  email: useEmailAddressSchema().notRequired(),
})
export const useInitialDomesticCommuneSchema = () => ({
  createInitialDomesticCommune: boolean(),
  commune: object().when(
    "createInitialDomesticCommune",
    ([createInitialDomesticCommune], schema) =>
      createInitialDomesticCommune
        ? schema.shape({
            ...useCommuneSchema(),
          })
        : schema
  ),
})

export type InitialDomesticCommuneSchema =
  | {
      createInitialDomesticCommune: false
      commune?: EntityWithoutRelations<Commune, "additionalAddress"> & AdditionalAddressForm
    }
  | {
      createInitialDomesticCommune: true
      commune: EntityWithoutRelations<Commune, "additionalAddress"> & AdditionalAddressForm
    }

export const requiredIfProvided = (fieldForValidation: string, message: string) => ({
  name: "isRequired",
  message,
  test: (value: string | undefined, context: TestContext): boolean => {
    const relatedField = context.parent[fieldForValidation]
    return !!value || !relatedField
  },
})

export const timeOrderTest = (
  fieldForValidation: string,
  message: string,
  dayName: string,
  isAfter = false
) => ({
  name: isAfter ? "isAfter" : "isBefore",
  message: i18n.global.t(message, { dayName: i18n.global.t(`weekdays.${dayName}`) }),
  test: (value: string | undefined, context: TestContext) => {
    return isAfter
      ? isTimeAfterTime(value, context.parent[fieldForValidation])
      : isTimeAfterTime(context.parent[fieldForValidation], value)
  },
})

export const useAddressSchema = () => ({
  firstname: string().trim().notRequired(),
  lastname: string().trim().required(i18n.global.t("validation.lastname_required")),
  contact: useContactSchema().notRequired(),
  financialAccountingData: object({
    fadNumber: string().notRequired(),
    objectNumber: string().notRequired(),
  }),
  collectiveInvoice: boolean().default(true),
  facility: string()
    .trim()
    .notRequired()
    .nullable()
    .max(80, i18n.global.t("validation.max_character", [80])),
  comments: string()
    .trim()
    .notRequired()
    .nullable()
    .max(100, i18n.global.t("validation.max_character", [100])),
})

export const useTimeSchema = () =>
  string().trim().matches(TIME_REGEX_OPTIONAL, i18n.global.t("validation.invalid_time"))

export const stepSchemas = [
  object({
    executionType: boolean().default(false),
    defaultAccountingGroupId: string().required(
      i18n.global.t("beitraege.beitragserhebung.validation.defaultAccountingGroupId")
    ),
    accountingDate: useDateSchema()
      .required(i18n.global.t("beitraege.beitragserhebung.validation.accountingDate"))
      .default(new Date()),
    receiptDate: useDateSchema()
      .required(i18n.global.t("beitraege.beitragserhebung.validation.receiptDate"))
      .default(new Date()),
    dueDateInitial: useDateSchema()
      .required(i18n.global.t("beitraege.beitragserhebung.validation.dueDate"))
      .default(new Date()),
    includeExemptChildren: boolean().default(false),
    isTestRun: boolean().optional().default(false),
  }),
  object({}),
  object({}),
  object({
    bankAccount: string().required(
      i18n.global.t("beitraege.beitragserhebung.validation.bankAccount")
    ),
    dueDateFinal: useDateSchema()
      .required(i18n.global.t("beitraege.beitragserhebung.validation.dueDate2"))
      .min(new Date(), i18n.global.t("validation.today_date_min")),
    purposeDate: string().required(
      i18n.global.t("beitraege.beitragserhebung.validation.purposeDate")
    ),
    markAsPaid: boolean().default(false),
  }),
  object({}),
]
export const appointmentTypeSchema = () =>
  string()
    .trim()
    .oneOf(
      Object.values(AppointmentType),
      i18n.global.t("validation.invalid_appointment_type", {
        subtypes: Object.values(AppointmentType).join(", "),
      })
    )

export const feeTypeSchema = () =>
  string()
    .trim()
    .oneOf(
      Object.values(FeeCategory),
      i18n.global.t("validation.invalid_fee_type_type", {
        subtypes: Object.values(FeeCategory).join(", "),
      })
    )

export const feeTypeGeneralCategorySchema = () =>
  string()
    .trim()
    .oneOf(
      Object.values(GeneralFeeCategory),
      i18n.global.t("validation.invalid_fee_type_general_category", {
        subtypes: Object.values(GeneralFeeCategory).join(", "),
      })
    )

export const accountingGroupRepetitionSchema = () =>
  string()
    .trim()
    .oneOf(
      Object.values(Repetition),
      i18n.global.t("validation.invalid_fee_type_general_category", {
        subtypes: Object.values(Repetition).join(", "),
      })
    )

export const accountingGroupMonthsSchema = () =>
  string()
    .trim()
    .oneOf(
      Object.values(Months),
      i18n.global.t("validation.invalid_accounting_group_month", {
        subtypes: Object.values(Months).join(", "),
      })
    )

export const institutionTypeSchema = () =>
  string()
    .trim()
    .oneOf(
      Object.values(InstitutionType),
      i18n.global.t("validation.invalid_institution_type", {
        subtypes: Object.values(InstitutionType).join(", "),
      })
    )

export const PaymentMethodSchema = () =>
  string()
    .trim()
    .oneOf(
      Object.values(PaymentMethod),
      i18n.global.t("validation.invalid_payment_method", {
        subtypes: Object.values(PaymentMethod).join(", "),
      })
    )

export const GroupRoleSchema = () =>
  string()
    .trim()
    .oneOf(
      Object.values(GroupRole),
      i18n.global.t("validation.invalid_group_role", {
        subtypes: Object.values(GroupRole).join(", "),
      })
    )

export const FactorCalculationBaseSchema = () =>
  string()
    .trim()
    .oneOf(
      Object.values(FactorCalculationBase),
      i18n.global.t("validation.invalid_factor_calculation_base", {
        subtypes: Object.values(FactorCalculationBase).join(", "),
      })
    )
